// Success screen — port of mockup KioskSuccess (pdks/kiosk.jsx, brand
// tone): check medallion, big serif confirmation with italic em, the
// punch time, a personal farewell and the auto-return note. Rendered
// inside <KioskShell tone="brand"> by App.

import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { KioskPunchResult } from "@/api/punch";

export interface SuccessScreenProps {
  readonly result: KioskPunchResult;
  readonly autoReturnSeconds: number;
  readonly onDone: () => void;
}

export function SuccessScreen({
  result,
  autoReturnSeconds,
  onDone,
}: SuccessScreenProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const isIn = result.action === "clock_in";

  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(result.at));

  const farewell = isIn
    ? result.employeeName !== null
      ? t("kiosk.success.farewell_in", { name: result.employeeName })
      : t("kiosk.success.farewell_in_anon")
    : result.employeeName !== null
      ? t("kiosk.success.farewell_out", { name: result.employeeName })
      : t("kiosk.success.farewell_out_anon");

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-16 pb-10 text-[var(--fg-on-accent)]">
      <div
        aria-hidden="true"
        className="mb-[30px] flex h-[148px] w-[148px] items-center justify-center rounded-full bg-[var(--fg-on-accent)]/20"
      >
        <Check size={84} strokeWidth={2.4} />
      </div>
      {/* role=status announces the recorded punch to screen readers, mirroring
          FailureScreen's role=alert. (SEM-324 audit) */}
      <h1
        role="status"
        className="m-0 text-center font-serif text-[clamp(44px,5.5vw,72px)] font-normal tracking-[-0.015em]"
      >
        {isIn ? t("kiosk.success.title_in") : t("kiosk.success.title_out")}{" "}
        <em className="italic">{t("kiosk.success.title_em")}</em>
      </h1>
      <p className="mt-[18px] font-sans text-[52px] font-semibold tracking-[-0.02em] tabular-nums">
        {time}
      </p>
      <p className="mt-2 font-serif text-[22px] italic text-[var(--fg-on-accent)]">{farewell}</p>
      <button
        type="button"
        onClick={onDone}
        className="mt-10 cursor-pointer rounded-full border-0 bg-[var(--bg-raised)] px-11 py-4 font-sans text-[17px] font-semibold text-[var(--accent)] transition-opacity focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] active:opacity-85"
      >
        {t("kiosk.success.done")}
      </button>
      <p className="mt-[22px] font-sans text-[14px] text-[var(--fg-on-accent)]">
        {t("kiosk.success.auto_return", { seconds: autoReturnSeconds })}
      </p>
    </div>
  );
}
